import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, MessageCircle, Star } from "lucide-react";
import client, { imgSrc } from "@/lib/api";
import { MaskedLine, Parallax, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import { STORES, fmtPrice, waLink } from "@/lib/constants";
import { useTexts } from "@/lib/texts";

const CHAPTERS = [
  {
    n: "01",
    title: "La valutazione onesta",
    text: "Pesiamo e valutiamo ogni oggetto davanti a te, con la massima trasparenza. Nessuna sorpresa: il prezzo che vedi è il prezzo che ricevi.",
  },
  {
    n: "02",
    title: "Il pagamento immediato",
    text: "In contanti o con bonifico istantaneo. Quando vendi il tuo oro da noi, il valore è subito nelle tue mani.",
  },
  {
    n: "03",
    title: "La seconda vita del prezioso",
    text: "Selezioniamo gioielli e orologi di prestigio come Rolex, Cartier, Bvlgari e molto altro, e li riportiamo al polso e al décolleté di chi saprà amarli.",
  },
];

const METALS = ["Oro", "Argento", "Gioielli", "Orologi", "Diamanti"];

function JewelCarousel({ items }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 3000);
    return () => clearInterval(t);
  }, [items.length]);

  if (!items.length) return null;
  const current = items[idx];

  return (
    <div data-testid="jewel-carousel">
      <Link to={`/prodotto/${current.id}`} data-testid="carousel-link" className="group block">
        <div className="relative aspect-square overflow-hidden bg-[#0a0a0a]">
          <AnimatePresence>
            <motion.img
              key={`${current.id}-${idx}`}
              src={imgSrc(current.image)}
              alt={current.name}
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.4, ease: "easeInOut" }}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </AnimatePresence>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between gap-4">
            <p className="font-serif text-lg leading-snug text-[#FAFAFA]" data-testid="carousel-product-name">{current.name}</p>
            <p className="shrink-0 text-gold" data-testid="carousel-product-price">€ {fmtPrice(current.price)}</p>
          </div>
        </div>
      </Link>
      <div className="mt-4 flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#737373]">Dalla nostra vetrina · tocca per i dettagli</p>
        <div className="flex gap-1.5" data-testid="carousel-dots">
          {items.map((it, i) => (
            <button
              key={it.id}
              onClick={() => setIdx(i)}
              aria-label={`Vai al gioiello ${i + 1}`}
              data-testid={`carousel-dot-${i}`}
              className={`h-1 transition-all duration-500 ${i === idx ? "w-6 bg-gold" : "w-2 bg-white/20 hover:bg-white/40"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// dissolvenza lenta tra gioielli del catalogo, con avvio sfalsato
function DissolveImage({ items, interval, className, alt, testid }) {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * Math.max(items.length, 1)));

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => {
      setIdx((i) => {
        let next;
        do {
          next = Math.floor(Math.random() * items.length);
        } while (next === i);
        return next;
      });
    }, interval);
    return () => clearInterval(t);
  }, [items.length, interval]);

  if (!items.length) return null;
  const current = items[idx];

  return (
    <div className="relative h-full w-full">
      <AnimatePresence>
        <motion.img
          key={`${current.id}-${idx}`}
          src={imgSrc(current.image)}
          alt={`${alt} — ${current.name}`}
          data-testid={testid}
          initial={{ opacity: 0, scale: 1.03 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 2, ease: "easeInOut" }}
          className={`absolute inset-0 ${className}`}
        />
      </AnimatePresence>
    </div>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [carousel, setCarousel] = useState([]);
  const [heroLarge, setHeroLarge] = useState([]);
  const [heroSmall, setHeroSmall] = useState([]);
  const texts = useTexts();
  const [reviewsData, setReviewsData] = useState({ rating: 4.7, count: 159, reviews: [] });
  const reviewsRef = useRef(null);

  useEffect(() => {
    client
      .get("/products")
      .then((r) => {
        const all = r.data;
        setFeatured(all.filter((p) => p.featured).slice(0, 6));
        const pool = [...all].sort(() => Math.random() - 0.5);
        setCarousel(pool.slice(0, 10));
        setHeroLarge([...all].sort(() => Math.random() - 0.5).slice(0, 8));
        setHeroSmall([...all].sort(() => Math.random() - 0.5).slice(0, 8));
      })
      .catch(() => {});
    client
      .get("/reviews")
      .then((r) => setReviewsData((d) => ({ ...r.data, rating: texts.reviews_rating, count: texts.reviews_count })))
      .catch(() => setReviewsData((d) => ({ ...d, rating: texts.reviews_rating, count: texts.reviews_count })));
  }, []);

  const scrollReviews = (dir) =>
    reviewsRef.current?.scrollBy({ left: dir * 340, behavior: "smooth" });

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen overflow-hidden pt-16 md:pt-20">
        <div className="gold-spotlight right-[-15%] top-[10%] h-[70vh] w-[70vw]" />
        <div className="mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl items-center gap-12 px-5 pb-16 pt-10 md:px-8 lg:grid-cols-12 lg:gap-8">
          <div className="relative z-10 lg:col-span-7">
            <Reveal delay={0.05}>
              <p className="mb-8 inline-flex items-center gap-3 border border-gold/30 px-4 py-2 text-[10px] uppercase tracking-[0.35em] text-gold">
                {texts.hero_tagline}
              </p>
            </Reveal>
            <h1 className="font-serif text-5xl leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl xl:text-[5.4rem]">
              <MaskedLine delay={0.15}>{texts.hero_line1}</MaskedLine>
              <MaskedLine delay={0.3}>
                <em className="text-gold-gradient not-italic italic">{texts.hero_line2_gold}</em> {texts.hero_line2_rest}
              </MaskedLine>
              <MaskedLine delay={0.45}>{texts.hero_line3}</MaskedLine>
            </h1>
            <Reveal delay={0.65}>
              <p className="mt-8 max-w-md text-base leading-relaxed text-[#A3A3A3] md:text-lg">
                {texts.hero_sub}
              </p>
            </Reveal>
            <Reveal delay={0.8}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/catalogo"
                  data-testid="hero-catalog-cta"
                  className="group inline-flex items-center gap-3 bg-gold px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-black transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover"
                >
                  Scopri il catalogo
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.95}>
              <div className="mt-14 flex flex-wrap gap-x-10 gap-y-4 border-t border-white/10 pt-6 text-[11px] uppercase tracking-[0.25em] text-[#A3A3A3]">
                <span><span className="text-gold">3</span> negozi</span>
                <span className="inline-flex items-center gap-1.5" data-testid="hero-rating">
                  <span className="text-gold">{texts.reviews_rating}</span>
                  <Star size={11} className="fill-gold text-gold" />
                  · {texts.reviews_count} recensioni
                </span>
                <span>Pagamento istantaneo</span>
              </div>
            </Reveal>
          </div>

          <div className="relative hidden lg:col-span-5 lg:block">
            <Parallax amount={45}>
              <div className="relative ml-auto w-[85%]">
                <div className="gold-spotlight inset-[-20%] h-[140%] w-[140%]" />
                <div className="relative aspect-[4/5] overflow-hidden bg-[#0a0a0a] p-3 shadow-[0_40px_120px_-30px_rgba(212,175,55,0.25)]">
                  <DissolveImage
                    items={heroLarge}
                    interval={5200}
                    className="h-full w-full object-cover"
                    alt="Gioiello della vetrina Oro Jewels"
                    testid="hero-image"
                  />
                </div>
                <p className="mt-3 text-right text-[10px] uppercase tracking-[0.3em] text-[#737373]">
                  Dalla nostra vetrina
                </p>
              </div>
            </Parallax>
            <div className="float-slow absolute -left-8 bottom-6 w-40 overflow-hidden border-2 border-gold/25 bg-[#0a0a0a] shadow-2xl">
              <div className="relative aspect-square w-full">
                <DissolveImage
                  items={heroSmall}
                  interval={7800}
                  className="h-full w-full object-cover"
                  alt="Gioiello Oro Jewels in evidenza"
                  testid="hero-image-small"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* MANIFESTO */}
      <section className="relative mx-auto max-w-7xl px-5 py-28 md:px-8 md:py-40" data-testid="manifesto-section">
        <Reveal>
          <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Il nostro manifesto</p>
        </Reveal>
        <div className="mt-14 space-y-24 md:space-y-32">
          {CHAPTERS.map((c, i) => (
            <div key={c.n} className="grid items-start gap-8 md:grid-cols-12" data-testid={`manifesto-chapter-${c.n}`}>
              <Reveal className={`md:col-span-4 ${i % 2 === 1 ? "md:order-2 md:text-right" : ""}`}>
                <span className="outline-number font-serif text-[6.5rem] leading-none md:text-[9rem]">{c.n}</span>
              </Reveal>
              <div className={`md:col-span-8 ${i % 2 === 1 ? "md:order-1" : ""} md:-ml-6 md:pt-10`}>
                <MaskedLine className="font-serif text-3xl md:text-5xl">{c.title}</MaskedLine>
                <Reveal delay={0.2}>
                  <p className="mt-6 max-w-xl text-base leading-relaxed text-[#A3A3A3] md:text-lg">{c.text}</p>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* NUOVI ARRIVI */}
      <section className="relative border-t border-white/10 bg-[#080808] py-28 md:py-36" data-testid="featured-section">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Reveal>
                <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Selezione del momento</p>
              </Reveal>
              <MaskedLine className="mt-4 font-serif text-4xl md:text-6xl">Nuovi arrivi</MaskedLine>
            </div>
            <Reveal delay={0.2}>
              <Link
                to="/catalogo"
                data-testid="featured-view-all-link"
                className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#A3A3A3] transition-colors hover:text-gold"
              >
                Vedi tutto il catalogo
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-14 grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-16 flex justify-end">
              <Link
                to="/catalogo"
                data-testid="featured-view-all-bottom"
                className="group inline-flex items-center gap-3 border border-gold/40 px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-gold transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-black"
              >
                Vedi tutto il catalogo
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COMPRO ORO BAND */}
      <section className="relative overflow-hidden py-28 md:py-40" data-testid="buy-gold-section">
        <div className="gold-spotlight left-[-10%] top-[-20%] h-[60vh] w-[50vw]" />
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 md:px-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Compro oro</p>
            </Reveal>
            <h2 className="mt-5 font-serif text-4xl leading-tight md:text-6xl">
              <MaskedLine delay={0.1}>{texts.buygold_title1}</MaskedLine>
              <MaskedLine delay={0.25}>
                {texts.buygold_title2a} <em className="text-gold-gradient">{texts.buygold_title2_gold}</em>{texts.buygold_title2b}
              </MaskedLine>
            </h2>
            <Reveal delay={0.3}>
              <p className="mt-7 max-w-lg text-base leading-relaxed text-[#A3A3A3] md:text-lg">
                {texts.buygold_sub}
              </p>
            </Reveal>
            <Reveal delay={0.4}>
              <div className="mt-9 flex flex-wrap gap-3">
                {METALS.map((m) => (
                  <span
                    key={m}
                    data-testid={`metal-chip-${m.toLowerCase()}`}
                    className="border border-gold/30 px-5 py-2.5 font-serif text-lg italic text-gold-pale"
                  >
                    {m}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.5}>
              <a
                href={waLink("Buongiorno, vorrei una valutazione gratuita per i miei oggetti preziosi.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="buy-gold-whatsapp-cta"
                className="mt-10 inline-flex items-center gap-3 bg-gold px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-black transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover"
              >
                <MessageCircle size={15} />
                Richiedi una valutazione gratuita
              </a>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <JewelCarousel items={carousel} />
            </Reveal>
          </div>
        </div>
      </section>

      {/* RECENSIONI */}
      <section className="border-t border-white/10 bg-[#080808] py-28 md:py-36" data-testid="reviews-section">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="min-w-0 lg:col-span-4">
              <Reveal>
                <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Dicono di noi</p>
              </Reveal>
              <MaskedLine className="mt-5 font-serif text-6xl md:text-7xl" delay={0.1}>
                {texts.reviews_rating}
              </MaskedLine>
              <Reveal delay={0.2}>
                <div className="mt-4 flex gap-1" data-testid="reviews-stars">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={18} className={i < Math.round(parseFloat(texts.reviews_rating) || 5) ? "fill-gold text-gold" : "fill-gold/40 text-gold/40"} />
                  ))}
                </div>
                <p className="mt-4 text-sm text-[#A3A3A3]" data-testid="reviews-count">
                  Basato su {texts.reviews_count} recensioni Google
                </p>
                <div className="mt-8 flex gap-3">
                  <button
                    onClick={() => scrollReviews(-1)}
                    data-testid="reviews-prev-button"
                    aria-label="Recensioni precedenti"
                    className="flex h-11 w-11 items-center justify-center border border-white/15 text-[#A3A3A3] transition-colors duration-300 hover:border-gold hover:text-gold"
                  >
                    <ChevronLeft size={17} />
                  </button>
                  <button
                    onClick={() => scrollReviews(1)}
                    data-testid="reviews-next-button"
                    aria-label="Recensioni successive"
                    className="flex h-11 w-11 items-center justify-center border border-white/15 text-[#A3A3A3] transition-colors duration-300 hover:border-gold hover:text-gold"
                  >
                    <ChevronRight size={17} />
                  </button>
                </div>
                <a
                  href="https://g.page/r/CQwXksy7eV0PEBM/review"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="leave-review-button"
                  className="mt-8 inline-flex items-center gap-3 border border-gold/40 px-6 py-3.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-gold transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-black"
                >
                  <Star size={13} className="fill-current" />
                  Lascia la tua recensione
                </a>
              </Reveal>
            </div>
            <div className="min-w-0 lg:col-span-8">
              <div
                ref={reviewsRef}
                data-lenis-prevent
                className="flex snap-x snap-mandatory gap-6 overflow-x-auto pb-4 [scrollbar-width:thin] [touch-action:pan-x_pan-y] [-webkit-overflow-scrolling:touch]"
                data-testid="reviews-scroller"
              >
                {reviewsData.reviews.map((r, i) => (
                  <blockquote
                    key={r.id || i}
                    className="flex h-full w-[300px] shrink-0 snap-start flex-col border border-white/10 bg-[#0d0d0d] p-7 sm:w-[340px]"
                    data-testid={`review-card-${i}`}
                  >
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, s) => (
                        <Star key={s} size={12} className="fill-gold text-gold" />
                      ))}
                    </div>
                    <p className="mt-4 flex-1 font-serif text-base italic leading-relaxed text-[#E5E5E5]">"{r.text}"</p>
                    <footer className="mt-5 text-[10px] uppercase tracking-[0.3em] text-gold">{r.author}</footer>
                  </blockquote>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEGOZI */}
      <section className="py-28 md:py-36" data-testid="stores-section">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Dove siamo</p>
          </Reveal>
          <MaskedLine className="mt-4 font-serif text-4xl md:text-6xl" delay={0.1}>{texts.stores_title}</MaskedLine>
          <div className="mt-14 grid gap-px bg-white/10 md:grid-cols-3">
            {STORES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.12} className="bg-[#050505]">
                <div className="group flex h-full flex-col p-8 md:p-10" data-testid={`store-card-${s.id}`}>
                  <span className="outline-number font-serif text-5xl">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-6 font-serif text-2xl">{s.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#A3A3A3]">
                    {s.address}
                    <br />
                    {s.zip}
                  </p>
                  <div className="mt-auto flex gap-5 pt-8 text-[10px] uppercase tracking-[0.25em]">
                    <a href={`tel:${s.tel}`} data-testid={`store-card-${s.id}-call`} className="text-gold transition-colors hover:text-gold-pale">
                      Chiama · {s.phone}
                    </a>
                    <a
                      href={s.map}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`store-card-${s.id}-map`}
                      className="text-[#A3A3A3] transition-colors hover:text-gold"
                    >
                      Indicazioni
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden border-t border-white/10 py-28 text-center md:py-40" data-testid="final-cta-section">
        <div className="gold-spotlight left-1/2 top-1/2 h-[60vh] w-[80vw] -translate-x-1/2 -translate-y-1/2" />
        <div className="relative mx-auto max-w-3xl px-5">
          <h2 className="font-serif text-4xl leading-tight md:text-6xl">
            <MaskedLine>Vieni a trovarci,</MaskedLine>
            <MaskedLine delay={0.15}>
              il resto lo facciamo <em className="text-gold-gradient">noi</em>.
            </MaskedLine>
          </h2>
          <Reveal delay={0.35}>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/contatti"
                data-testid="final-cta-contacts"
                className="inline-flex items-center gap-3 bg-gold px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-black transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover"
              >
                Contattaci
                <ArrowRight size={15} />
              </Link>
              <a
                href="https://www.instagram.com/orojewels.porpora"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="final-cta-instagram"
                className="inline-flex items-center gap-3 border border-white/20 px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] transition-all duration-300 hover:-translate-y-0.5 hover:border-gold hover:text-gold"
              >
                Seguici su Instagram
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
